import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Form, FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'

import { ExternalLink } from '../../../global/ExternalLink'
import { Loading } from '../../../global/Loading/Loading'
import { Button } from '../../../global/button/Button'
import { useAccountInfo } from '../../../global/context/AccountInfoContext/AccountInfoContext.Provider'
import { useProductReadContext } from '../../../global/context/ProductContext'
import { useSessionLanguage } from '../../../global/context/SessionSettingsContext'
import { RawFormCheckBoxField } from '../../../global/formField/FormCheckBoxField'
import { createFormField } from '../../../global/formField/FormField'
import IconButton from '../../../global/iconButton/IconButton'
import { RadioButton } from '../../../global/radioButton/RadioButton'
import { FormTemplate } from '../../../global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '../../../global/templates/TransactionFormActionButtonTemplate'
import { useFormatNumber } from '../../../hooks/useFormatNumber'
import { DropArrowDownIcon } from '../../../icons/DropArrowDownIcon'
import { DropArrowUpIcon } from '../../../icons/DropArrowUpIcon'
import { InfoIcon } from '../../../icons/InfoIcon'
import { PlatformInfoIcon } from '../../../icons/PlatformInfoIcon'
import { AccountDetailedDto } from '../../../model/AccountDetailedDto'
import { AccountTypeEnum } from '../../../model/AccountGroupType'
import { CampaignCalculationType } from '../../../model/CampaignCalculationType'
import {
  CreateCampaignTradingAccount,
  CreateTradingAccount,
} from '../../../model/CreateTradingAccount'
import { LeverageTypeDto, isLeverageTypeDynamic } from '../../../model/LeverageTypeDto'
import { NameDto } from '../../../model/NameDto'
import {
  PlatformTypeEnum,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '../../../model/PlatformTypeEnum'
import { TradingAccountPlatformTypesOptions } from '../../../model/TradingAccountCreationOptions'
import { TradingAccountInfo } from '../../../model/TradingAccountInfo'
import { CurrencyType, WalletDto } from '../../../model/WalletDto'
import { useNavigate } from '../../../navigation/custom-react-router-dom'
import { InfoCard } from '../../../ui/Popups/InfoCard/InfoCard'
import { TextSmall, TextStrong } from '../../../ui/Typography/Typography'
import { useApiClient } from '../../../utils/ApiClient'
import { useWalletsReadContext } from '../../../utils/UserWalletContext'
import { ClientApiClient } from '../../../utils/clientApi'
import {
  isTickmillAS,
  isTickmillEU,
  isTickmillSC,
  isTickmillUK,
} from '../../../utils/companyName.utils'
import { useWindowResize } from '../../../utils/domUtils'
import { FormSubmitValues, IBPattern } from '../../../utils/formValidation'
import { isOne } from '../../../utils/validations'
import { useLeverageSelect } from '../CreateDemoTradingAccount/CreateDemoTradingAccountForm'
import { filterTradingAccountWallets } from '../TradingAccountWalletModal'
import { OuterProps } from './CreateTradingAccountCampaign.types'
import { CreateTradingAccountCampaignModals, useModals } from './CreateTradingAccountCampaignModals'

import styles from '../TradingAccountsPage.module.scss'

export interface CreateTradingAccountCampaignFormValues {
  tradingAccount: CampaignExtendedName
  wallet: WalletDto | undefined
  currencyId: CurrencyType | undefined
  calculationType: NameDto<CampaignCalculationType> | undefined
  platformType: NameDto<PlatformTypeEnum> | undefined
  accountType: NameDto | undefined
  leverageType: LeverageTypeDto | undefined
  leverageTypes: LeverageTypeDto[]
  receivesReports: boolean
  introducingBroker: undefined | { id: string; name: string; isCustomSpreadMarkup?: boolean }
  campaignId: string | null
  ibCode: string
  referralOptionId: GenericReferralType | string
  terms: boolean
  markup: boolean
  ibState: NameDto<string>
}

type CampaignExtendedName = { id: string; name: string; platformTypes: NameDto[] } | undefined

export type CampaignExtendedNameDto = Omit<NameDto, 'id'> & {
  id: string
  termsAndConditions?: string
  isCampaign?: boolean
  type?: NameDto
  calculationType?: NameDto<CampaignCalculationType>
  platformTypes: NameDto[]
  campaignDescription?: string
}

const FormField = createFormField<CreateTradingAccountCampaignFormValues>()

type CreateTradingAccountCampaignFormProps = FormikProps<CreateTradingAccountCampaignFormValues> &
  OuterProps

const useAccountTypesFetch = (props: CreateTradingAccountCampaignFormProps) => {
  const { values } = props

  const apiClient = useApiClient(ClientApiClient)
  const [accountTypes, setAccountTypes] = useState<NameDto<number>[]>([])

  const fetchAccountTypes = async () => {
    if (values.platformType?.id) {
      if (values.wallet?.currency?.id) {
        setAccountTypes(
          await apiClient.getAccountType(values.platformType.id, values.wallet.currency.id)
        )
      } else if (values?.currencyId) {
        setAccountTypes(await apiClient.getAccountType(values.platformType.id, values.currencyId))
      }
    }
  }

  useEffect(() => {
    if (values.platformType?.id && (values.wallet?.currency?.id || values.currencyId)) {
      fetchAccountTypes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.wallet?.currency, values.platformType, values.currencyId])

  return { fetchAccountTypes, accountTypes, setAccountTypes }
}

export const useTradingAccountCreationOptionsFetch = (
  props: CreateTradingAccountCampaignFormProps
) => {
  const { values } = props

  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()
  const { accountInfo } = useAccountInfo()

  const [campaignType, setCampaignType] = useState<NameDto | undefined>()
  const [campaignCurrencies, setCampaignCurrencies] = useState<CurrencyType[]>([])
  const [platformTypes, setPlatformTypes] = useState<TradingAccountPlatformTypesOptions>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setCampaignType(props.data.find((campaign) => campaign.id === values.campaignId)?.type)
    fetchTradingAccountCreationOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.campaignId, values.platformType])

  const fetchTradingAccountCreationOptions = async () => {
    setIsLoading(true)

    const getRegularAccountPlatforms = async () => {
      const filteredPlatforms = accountInfo
        ?.filter((accountInfo) => accountInfo.product.id === product)
        .map((accountInfo) => ({ id: accountInfo.platform.id, name: accountInfo.platform.name }))
      setPlatformTypes(filteredPlatforms ?? [])
    }

    try {
      if (values.campaignId) {
        const creationOptions = await apiClient.getTradingAccountCreationOptions(
          values.campaignId,
          {
            platformTypeId: values.platformType?.id.toString(),
          }
        )
        setCampaignCurrencies(creationOptions.currencies)
        setPlatformTypes(creationOptions.platformTypes)
      } else {
        getRegularAccountPlatforms()
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    campaignType,
    campaignCurrencies,
    platformTypes,
    isLoading,
  }
}

const useValidateAccountIntroducingBroker = (
  props: CreateTradingAccountCampaignFormProps,
  setAllFieldsDisabled: (value: boolean) => void,
  setIsValidatingIBCode: (value: boolean) => void
) => {
  const { values, errors, setFieldValue, setFieldError } = props

  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)

  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout

    if (values.ibCode && !errors.ibCode) {
      setAllFieldsDisabled(true)

      delayDebounceFn = setTimeout(async () => {
        setIsValidatingIBCode(true)

        try {
          const response = await apiClient.validateAccountIntroducingBrokerByCode(values.ibCode)
          if (response.isCustomSpreadMarkup) {
            // make this field unchecked to force user to check this one, by default it's checked
            // but we show it only when isCustomSpreadMarkup is true
            setFieldValue('markup', false)
          }
          setFieldValue('introducingBroker', response)

          setFieldError('ibCode', '')
          setAllFieldsDisabled(false)
        } catch {
          setAllFieldsDisabled(true)
          setFieldError('ibCode', t('Referral code does not exist'))
        } finally {
          setIsValidatingIBCode(false)
        }
      }, 1500)
    }

    return () => clearTimeout(delayDebounceFn)
  }, [
    apiClient,
    errors.ibCode,
    setAllFieldsDisabled,
    setFieldError,
    setFieldValue,
    setIsValidatingIBCode,
    t,
    values.ibCode,
  ])
}

export const usePlatformData = (
  props: CreateTradingAccountCampaignFormProps,
  platformTypes: TradingAccountPlatformTypesOptions = [],
  allFieldsDisabled: boolean
) => {
  const { values, account } = props
  const { product } = useProductReadContext()
  const { accountInfo } = useAccountInfo()
  const { accountTypes = [] } = useAccountTypesFetch(props)

  const isCampaign = useMemo(
    () => values.campaignId && values.campaignId !== '0',
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [values.campaignId]
  )

  const accountFilteredPlatforms = useMemo(
    () =>
      accountInfo
        // we need maxAccountsAllowed check when it's a campaign account (https://tickmill.atlassian.net/browse/WCA-3922)
        .filter((x) => isCampaign || x.maxAccountsAllowed > 0)
        .filter((x) =>
          platformTypes.length ? platformTypes.map((x) => x.id).includes(x.platform.id) : true
        )
        .filter((x) => x.product.id === product)
        .filter((x) => {
          if (isTMTPlatformType(x.platform.id) || isTradingViewPlatformType(x.platform.id)) {
            return isTickmillSC(account) || isTickmillAS(account)
          } else {
            return true
          }
        }),
    [accountInfo, platformTypes, product, account, isCampaign]
  )

  const rearrangedTradingPlatforms: TradingAccountInfo[] = useMemo(
    () =>
      platformTypes
        .map((platformType) => {
          const matchingTradingPlatform = accountFilteredPlatforms.find(
            ({ platform }) => platform.id === platformType.id
          )
          return matchingTradingPlatform ? matchingTradingPlatform : null
        })
        .filter((item): item is TradingAccountInfo => Boolean(item)),
    [accountFilteredPlatforms, platformTypes]
  )

  const platformsAllowToOpenAccount = useMemo(
    // for campaign accounts, consider all platforms as allowed, regardless of allowToOpenAccount flag (https://tickmill.atlassian.net/browse/WCA-3922)
    () =>
      isCampaign
        ? rearrangedTradingPlatforms
        : rearrangedTradingPlatforms.filter((x) => x.allowToOpenAccount),
    [rearrangedTradingPlatforms, isCampaign]
  )

  const platformAllowToOpenAccount = useMemo(
    () => platformsAllowToOpenAccount[0],
    [platformsAllowToOpenAccount]
  )

  const platformsDisallowToOpenAccount = useMemo(
    // for campaign accounts consider no platforms as not allowed (https://tickmill.atlassian.net/browse/WCA-3922)
    () => (isCampaign ? [] : rearrangedTradingPlatforms.filter((x) => !x.allowToOpenAccount)),
    [rearrangedTradingPlatforms, isCampaign]
  )

  const isAccountTypeDisabled = useMemo(
    () =>
      !isCampaign
        ? !values.wallet || allFieldsDisabled
        : !values.currencyId || !values.platformType || allFieldsDisabled,
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [isCampaign, values.wallet, values.currencyId, values.platformType, allFieldsDisabled]
  )

  const isWalletDisabled = useMemo(() => {
    return !values.platformType || allFieldsDisabled
  }, [values.platformType, allFieldsDisabled])

  const isLeverageDisabled = useMemo(() => {
    if (values.leverageType) {
      return false
    }

    return !isCampaign
      ? !values.wallet ||
          !values.platformType ||
          !values.accountType ||
          allFieldsDisabled ||
          isTMTPlatformType(values.platformType.id) ||
          isTradingViewPlatformType(values.platformType.id)
      : !values.currencyId ||
          !values.platformType ||
          !values.accountType ||
          allFieldsDisabled ||
          isTMTPlatformType(values.platformType.id) ||
          isTradingViewPlatformType(values.platformType.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCampaign,
    values.leverageType,
    values.wallet,
    values.currencyId,
    values.platformType,
    values.accountType,
    allFieldsDisabled,
  ])

  const accountTypesData = useMemo(
    () => {
      const isAccountFromBrazil = account?.addresses?.some(
        (address) => address?.country?.id === 'BRA'
      )

      const classicAccountType = accountTypes?.find(
        (accountType) => accountType?.id === AccountTypeEnum.Classic
      )

      const nonClassicAccountTypes = accountTypes?.filter(
        (accountType) => accountType?.id !== AccountTypeEnum.Classic
      )

      const accountTypesFilter = classicAccountType
        ? [classicAccountType, ...nonClassicAccountTypes]
        : nonClassicAccountTypes

      return isAccountFromBrazil ? accountTypesFilter : accountTypes
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [account?.addresses, accountTypes]
  )

  return {
    isCampaign,
    isWalletDisabled,
    isLeverageDisabled,
    isAccountTypeDisabled,
    accountTypes: accountTypesData,
    platformsAllowToOpenAccount,
    platformAllowToOpenAccount,
    platformsDisallowToOpenAccount,
    isOnlyOnePlatformsAllowToOpenAccount: isOne(platformsAllowToOpenAccount.length),
  }
}

const CreateTradingAccountCampaignFormUI: React.FC<CreateTradingAccountCampaignFormProps> = (
  props
) => {
  const { isSubmitting, values, isValid } = props
  const { isCurrenciesLoading, account } = props
  const { setFieldValue } = props
  const { handleSubmit } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { formatMoney } = useFormatNumber()
  const navigate = useNavigate()

  const [isValidatingIBCode, setIsValidatingIBCode] = useState(false)
  const [isReferralSectionExpanded, setIsReferralSectionExpanded] = useState(false)
  const [allFieldsDisabled, setAllFieldsDisabled] = useState(false)

  const tradingAccountCreationOptionsFetchProps = useTradingAccountCreationOptionsFetch(props)
  const { campaignType, campaignCurrencies } = tradingAccountCreationOptionsFetchProps
  const { isLoading: isTACreationOptionsLoading } = tradingAccountCreationOptionsFetchProps

  const platformDataProps = usePlatformData(
    props,
    tradingAccountCreationOptionsFetchProps.platformTypes,
    allFieldsDisabled
  )
  const { isCampaign, isWalletDisabled, isLeverageDisabled, isAccountTypeDisabled } =
    platformDataProps
  const { platformAllowToOpenAccount, platformsAllowToOpenAccount } = platformDataProps
  const { platformsDisallowToOpenAccount, isOnlyOnePlatformsAllowToOpenAccount } = platformDataProps

  const modalsProps = useModals(props, platformDataProps, tradingAccountCreationOptionsFetchProps)
  const { setAccountTypeModalOpen, setMarkupModalOpen, setCurrencyModalOpen, setCancelModalOpen } =
    modalsProps
  const { setPlatformTypeModalOpen, setRegularCampaignAccountTypeModalOpen, setLeverageModalOpen } =
    modalsProps
  const { tmpPlatformType, setWalletModalOpen, setDynamicLeverageModalOpen } = modalsProps

  const { title, renderReferral } = useData(props, platformDataProps)
  useValidateAccountIntroducingBroker(props, setAllFieldsDisabled, setIsValidatingIBCode)

  const { isLoading, isPlatformFormDisabled, handleSetTradingPlatform } = usePreselectEffect(
    props,
    platformDataProps,
    tradingAccountCreationOptionsFetchProps,
    modalsProps
  )

  const handleSubmitForm = async () => {
    setMarkupModalOpen(false)
    handleSubmit()
  }

  const hasReachedMaxAccounts = useMemo(
    () => platformsDisallowToOpenAccount.length > 0,
    [platformsDisallowToOpenAccount]
  )

  const toggleReferralSection = () => {
    setIsReferralSectionExpanded((prev) => !prev)
  }

  const handleSelectPlatformType = () => {
    if (!isMobile) {
      setPlatformTypeModalOpen(true)
      return
    }

    navigate(`/dashboard/traders-room/trading-accounts/new/platform-types`, {
      replace: true,
      state: {
        tradingAccountsAccountInfo: platformsAllowToOpenAccount,
        demo: false,
        tmpPlatformType,
        previousValues: values,
      },
    })
  }

  const isCurrencyFieldDisabled = allFieldsDisabled || !values.platformType || isCurrenciesLoading

  return (
    <>
      <CreateTradingAccountCampaignModals
        {...props}
        {...modalsProps}
        onTradingPlatformSubmit={handleSetTradingPlatform}
        onSubmitForm={handleSubmitForm}
      />
      <Loading showLoadingIcon isLoading={isLoading}>
        <FormTemplate title={title} goBack={() => setCancelModalOpen(true)}>
          <Form>
            <div className={styles.formContainer}>
              {!isTickmillEU(props.account) && !isTickmillUK(props.account) && (
                <NameField
                  data={props.data}
                  campaignId={values.campaignId}
                  calculationType={values.calculationType}
                  campaignType={campaignType}
                  allFieldsDisabled={allFieldsDisabled}
                  onRegularCampaignAccountTypeModalOpen={() =>
                    setRegularCampaignAccountTypeModalOpen(true)
                  }
                />
              )}

              <FormField
                name='platformType.name'
                label={t('Trading Account.Trading Platform')}
                placeholder={t('Trading Account.Trading Platform')}
                rightIcon={<DropArrowDownIcon />}
                disabled={allFieldsDisabled || isPlatformFormDisabled || isTACreationOptionsLoading}
                loading={isTACreationOptionsLoading}
                readOnly
                required
                onClick={handleSelectPlatformType}
                key={values.platformType?.name ? 'platformType' : 'no-platformType'}
              />
              {hasReachedMaxAccounts && (
                <div className={styles.platformInfo}>
                  <PlatformInfoIcon />
                  <p className='ml-1'>
                    <span>
                      {t(
                        'Trading Account.You have reached the maximum amount of Live Trading Accounts for platform'
                      )}
                    </span>
                    <span className={styles.platformInfoStrong}>
                      {' '}
                      {platformsDisallowToOpenAccount.map((x) => x.platform.name).join(', ')}
                    </span>
                  </p>
                </div>
              )}
              {!isCampaign && (
                <FormField
                  name='wallet.name'
                  label={t('Trading Account.Wallet')}
                  placeholder={t('Trading Account.Wallet')}
                  disabled={isWalletDisabled}
                  required
                  value={
                    values.wallet
                      ? `${t('Trading Account.Wallet')} ${values?.wallet?.currency?.id} (${
                          values?.wallet?.name
                        })`
                      : undefined
                  }
                  rightIcon={<DropArrowDownIcon />}
                  readOnly
                  hint={
                    values.wallet
                      ? `${t('Wallet.Balance')} ${formatMoney(
                          values.wallet.balance,
                          values.wallet.currency.id
                        )}`
                      : null
                  }
                  onClick={() => setWalletModalOpen(true)}
                  key={values.wallet ? 'has-wallet' : 'no-wallet'}
                />
              )}
              {isCampaign && (
                <FormField
                  name='currencyId'
                  label={t('Campaigns.Currency')}
                  placeholder={t('Campaigns.Currency')}
                  value={values.currencyId}
                  disabled={isCurrencyFieldDisabled}
                  loading={isCurrenciesLoading}
                  required
                  rightIcon={<DropArrowDownIcon />}
                  readOnly
                  onClick={() => setCurrencyModalOpen(true)}
                  validateOnMount
                  key={values.currencyId ? 'currencyId' : 'no-currencyId'}
                />
              )}
              <FormField
                name='accountType.name'
                label={t('Trading Account.Account Type')}
                placeholder={t('Trading Account.Account Type')}
                rightIcon={<DropArrowDownIcon />}
                readOnly
                required
                disabled={isAccountTypeDisabled || isCurrenciesLoading}
                onClick={() => setAccountTypeModalOpen(true)}
                key={values.accountType?.id ? 'accountType' : 'no-accountType'}
              />
              <FormField
                name='leverageType.name'
                label={t('Trading Account.Leverage')}
                placeholder={t('Trading Account.Leverage')}
                rightIcon={<DropArrowDownIcon />}
                disabled={isLeverageDisabled}
                readOnly
                required
                onClick={() => setLeverageModalOpen(true)}
                hint={
                  isLeverageTypeDynamic(values.leverageType?.id) ? (
                    <span
                      onClick={(e) => {
                        e.stopPropagation()
                        setDynamicLeverageModalOpen(true)
                      }}
                      className='is-link'
                    >
                      {t('Trading Account.Leverage Information')}
                    </span>
                  ) : undefined
                }
                key={values.leverageType?.id ? 'leverageType' : 'no-leverageType'}
              />
              {/*show this field when we have new IB selected*/}
              {values.introducingBroker?.isCustomSpreadMarkup && (
                <RawFormCheckBoxField name='markup' disabled={!!allFieldsDisabled}>
                  {t('Trading Account.Markup Modal')}
                </RawFormCheckBoxField>
              )}
              {values.campaignId && (
                <RawFormCheckBoxField
                  name='terms'
                  disabled={!!allFieldsDisabled}
                  onChange={() => {
                    setFieldValue('terms', !values.terms)
                  }}
                >
                  {t('I have read all instructions and agree with')}{' '}
                  <ExternalLink
                    title={t('Terms & Conditions')}
                    url={props.data.find((x) => x.id === values?.campaignId)?.termsAndConditions}
                    className='is-link'
                  >
                    {t('Terms & Conditions')}
                  </ExternalLink>{' '}
                  {t('of the campaign')}
                </RawFormCheckBoxField>
              )}

              {renderReferral && (
                <>
                  <div className={styles.referralTitleWrapper}>
                    <IconButton className='has-cursor-pointer' onClick={toggleReferralSection}>
                      <div className={styles.toggleButton}>
                        {isReferralSectionExpanded ? <DropArrowUpIcon /> : <DropArrowDownIcon />}
                      </div>
                      <TextStrong className={styles.textColor}>
                        {t('Trading Account.Referral Code')}
                      </TextStrong>
                    </IconButton>
                    <div className={styles.infoButton}>
                      <InfoCard
                        title={t('Trading Account.Referral Code')}
                        text={
                          <TextSmall className={styles.textSecondary}>
                            {t(
                              'Trading Account.Referrals are agents who introduce new clients to Tickmill'
                            )}{' '}
                            {t(
                              'Trading Account.Enter the code that was provided to you by your Referral. The code consists of a combination of letters and digits.'
                            )}
                          </TextSmall>
                        }
                      >
                        <InfoIcon />
                      </InfoCard>
                    </div>
                  </div>
                  {isReferralSectionExpanded && (
                    <div className={styles.radioGroup}>
                      {createReferralOptions(props)
                        .filter(filterReferralOptions(props))
                        .map((referral) => (
                          <ReferralField
                            referral={referral}
                            account={account}
                            platformAllowToOpenAccount={platformAllowToOpenAccount}
                            isOnlyOnePlatformsAllowToOpenAccount={
                              isOnlyOnePlatformsAllowToOpenAccount
                            }
                            isValidatingIBCode={isValidatingIBCode}
                            setAllFieldsDisabled={setAllFieldsDisabled}
                          />
                        ))}
                    </div>
                  )}
                </>
              )}
              <IBCodeField onChange={() => setAllFieldsDisabled(true)} />
              <TransactionFormActionButtonTemplate>
                <Button
                  appearance='secondary'
                  size='L'
                  type='button'
                  onClick={() => setCancelModalOpen(true)}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  loading={isSubmitting || isValidatingIBCode}
                  appearance='primary'
                  size='L'
                  type='button'
                  onClick={async () => {
                    if (values.introducingBroker?.isCustomSpreadMarkup) {
                      setMarkupModalOpen(true)
                    } else {
                      await handleSubmitForm()
                    }
                  }}
                  disabled={!isValid || allFieldsDisabled}
                >
                  {t('Confirm')}
                </Button>
              </TransactionFormActionButtonTemplate>
            </div>
          </Form>
        </FormTemplate>
      </Loading>
    </>
  )
}

const usePreselectEffect = (
  props: CreateTradingAccountCampaignFormProps,
  platformDataProps: ReturnType<typeof usePlatformData>,
  tradingAccountCreationOptionsFetchProps: ReturnType<typeof useTradingAccountCreationOptionsFetch>,
  modalsProps: ReturnType<typeof useModals>
) => {
  const { values, setFieldValue, validateForm, setSelectedPlatform, setTouched } = props
  const { platformsAllowToOpenAccount, accountTypes } = platformDataProps
  const { campaignCurrencies } = tradingAccountCreationOptionsFetchProps
  const { tmpPlatformType, setPlatformTypeModalOpen } = modalsProps

  const isMobile = useWindowResize()
  const location = useLocation()
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()

  const { wallets = [] } = useWalletsReadContext()

  const [isLoading, setIsLoading] = useState(true)
  const [isPlatformFormDisabled, setIsPlatformFormDisabled] = useState(false)

  const { leverageType } = useLeverageSelect(values.platformType)

  useEffect(() => {
    setIsLoading(true)
    if (platformsAllowToOpenAccount.length === 0) {
      setIsPlatformFormDisabled(true)
      handleSetTradingPlatform(undefined)
    }
    if (platformsAllowToOpenAccount.length <= 1) {
      const [allowedPlatform] = platformsAllowToOpenAccount
      setIsPlatformFormDisabled(true)
      if (allowedPlatform) {
        handleSetTradingPlatform(allowedPlatform.platform)
      }
    } else {
      setIsPlatformFormDisabled(false)
    }

    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformsAllowToOpenAccount, locale])

  const handleSetTradingPlatform = async (platform?: NameDto) => {
    const currentPlatformType = tmpPlatformType || platform
    setPlatformTypeModalOpen(false)
    setFieldValue('wallet', undefined)
    setFieldValue('platformType', currentPlatformType)
    setSelectedPlatform(currentPlatformType)
    // TODO add when IB is set
    await refreshLeverageType(currentPlatformType?.id)
    if (currentPlatformType) {
      await props.onChangePlatform(currentPlatformType?.id)
    }
  }

  const refreshLeverageType = async (platformId: PlatformTypeEnum | undefined) => {
    const leverageTypes = await apiClient.getLeverageTypes(platformId)
    if (isTMTPlatformType(platformId) || isTradingViewPlatformType(platformId)) {
      const dynamicLeverage = leverageTypes.find((leverage) => isLeverageTypeDynamic(leverage.id))
      if (dynamicLeverage) {
        setFieldValue('leverageType', dynamicLeverage)
      } else {
        selectFirstLeverageType(leverageTypes)
      }
    } else {
      selectFirstLeverageType(leverageTypes)
    }
  }

  const selectFirstLeverageType = (leverageTypes: LeverageTypeDto[]) => {
    if (isOne(leverageTypes.length)) {
      const [leverageType] = leverageTypes
      setFieldValue('leverageType', leverageType)
      setFieldValue('leverageTypes', leverageTypes)
    } else {
      setFieldValue('leverageType', undefined)
      setFieldValue('leverageTypes', leverageTypes)
    }
  }

  useEffect(() => {
    selectFirstCurrency()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignCurrencies])

  const selectFirstCurrency = () => {
    if (isOne(campaignCurrencies.length)) {
      const [campaignCurrency] = campaignCurrencies
      setFieldValue('currencyId', campaignCurrency)
    } else {
      setFieldValue('currencyId', undefined)
    }
  }

  useEffect(() => {
    values?.platformType?.id && selectFirstWallet()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currencies, values?.platformType?.id])

  const selectFirstWallet = () => {
    const newWallets = filterTradingAccountWallets(wallets, props.currencies)
    const [wallet] = newWallets
    const newWallet = isOne(newWallets?.length) ? wallet : undefined

    if (newWallet) {
      const currency = props.currencies.find((x) => x.id === newWallet.currency.id)
      newWallet && currency && setFieldValue('wallet', { ...newWallet, currency })
    } else {
      setFieldValue('wallet', undefined)
    }
  }

  useEffect(() => {
    values?.wallet?.id && selectFirstAccountType()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.wallet?.id, accountTypes])

  const selectFirstAccountType = () => {
    const [accountType] = accountTypes
    const newAccountType = isOne(accountTypes.length) ? accountType : undefined
    setFieldValue('accountType', newAccountType)
  }

  useEffect(() => {
    const updateLeverage = async () => {
      if (values?.accountType?.id && values.platformType?.id && !values.leverageType?.id) {
        const newLeverageType = leverageType
        setFieldValue('leverageType', newLeverageType)
      }
    }
    updateLeverage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.accountType?.id, leverageType])

  useEffect(() => {
    restorePreviousValues()
  }, [location.state?.selectedPlatformType])

  const restorePreviousValues = () => {
    if (!isMobile || !location.state?.previousValues || !location.state?.selectedPlatformType) {
      return
    }

    if (values.platformType?.id !== location.state.selectedPlatformType.id) {
      setFieldValue('platformType', location.state.selectedPlatformType, false)
    }

    if (location.state.previousValues.platformType?.id === location.state.selectedPlatformType.id) {
      if (values.wallet?.id !== location.state.previousValues.wallet?.id) {
        setFieldValue('wallet', location.state.previousValues.wallet, false)
      }
      if (values.leverageType?.id !== location.state.previousValues.leverageType?.id) {
        setFieldValue('leverageType', location.state.previousValues.leverageType, false)
      }
      if (!values.accountType) {
        setFieldValue('accountType', location.state.previousValues.accountType, false)
      }
    }

    setTouched(
      {
        platformType: true,
        wallet: true,
        leverageType: true,
        accountType: true,
      },
      true
    )

    return { handleSetTradingPlatform, isPlatformFormDisabled, isLoading }
  }

  useEffect(() => {
    isMobile && validateForm()
  }, [values.platformType, values.wallet, values.leverageType, values.accountType])

  useEffect(() => {
    validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.referralOptionId])

  return { handleSetTradingPlatform, isLoading, isPlatformFormDisabled }
}

const useData = (
  props: CreateTradingAccountCampaignFormProps,
  platformDataProps: ReturnType<typeof usePlatformData>
) => {
  const { account, hasAnyLiveTA } = props
  const { isCampaign } = platformDataProps

  const { t } = useTranslation()

  const title = isCampaign
    ? t('Trading Account.Add Campaign Trading Account')
    : t('Trading Account.Add Trading Account')

  const renderReferral = useMemo(() => {
    if (isTickmillUK(account)) {
      if (!account?.introducingBroker) {
        return false
      }
    }

    if (isTickmillEU(account)) {
      return false
    }

    if (account?.affiliate) {
      return false
    }

    if (!hasAnyLiveTA && account?.introducingBroker) {
      return false
    }

    return true
  }, [account, hasAnyLiveTA])

  return { title, renderReferral }
}

interface IBCodeFieldProps {
  onChange(): void
}

const IBCodeField: React.FC<IBCodeFieldProps> = (props) => {
  const { onChange } = props

  const { t } = useTranslation()

  const context = useFormikContext<CreateTradingAccountCampaignFormValues>()
  const { values } = context

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      context.setFieldError('ibCode', '')
      onChange()
    }
    context.setFieldValue('ibCode', event.target.value)
  }

  if (values?.ibState?.id?.toString() === '0') {
    return null
  }

  if (values?.ibState?.id?.toString() === '1') {
    return (
      <FormField
        name='ibCode'
        label={t('Trading Account.Referral Code')}
        placeholder={t('Trading Account.Referral Code')}
        required
        onChange={handleChange}
      />
    )
  }

  return (
    <FormField
      name='ibCode'
      label={t('Trading Account.Referral Code')}
      placeholder={t('Trading Account.Referral Code')}
      required
      disabled
    />
  )
}

interface NameFieldProps {
  data: CampaignExtendedNameDto[]
  allFieldsDisabled: boolean
  campaignId: string | null
  calculationType: NameDto | undefined
  campaignType: NameDto | undefined
  onRegularCampaignAccountTypeModalOpen(): void
}

const NameField: React.FC<NameFieldProps> = (props) => {
  const { data, campaignId, allFieldsDisabled, onRegularCampaignAccountTypeModalOpen } = props

  const { t } = useTranslation()

  const handleRegularCampaignAccountTypeModalOpen = () => {
    onRegularCampaignAccountTypeModalOpen()
  }

  const getHintText = () => {
    return data.find((c) => c.id === campaignId)?.campaignDescription || ''
  }

  return (
    <FormField
      name='tradingAccount.name'
      type='text'
      label={t('Trading Account.Trading Account Type')}
      placeholder={t('Trading Account.Trading Account Type')}
      rightIcon={<DropArrowDownIcon />}
      disabled={allFieldsDisabled || isOne(data.length)}
      required
      readOnly
      onClick={handleRegularCampaignAccountTypeModalOpen}
      hint={getHintText()}
    />
  )
}

interface ReferralFieldProps {
  referral: NameDto<string>
  platformAllowToOpenAccount: TradingAccountInfo
  isOnlyOnePlatformsAllowToOpenAccount: boolean
  account: AccountDetailedDto | undefined
  isValidatingIBCode: boolean
  setAllFieldsDisabled(value: boolean): void
}

const ReferralField: React.FC<ReferralFieldProps> = (props) => {
  const { platformAllowToOpenAccount, isOnlyOnePlatformsAllowToOpenAccount } = props
  const { account, referral, isValidatingIBCode } = props
  const { setAllFieldsDisabled } = props

  const { t } = useTranslation()
  const { values, setFieldValue, setFieldError } =
    useFormikContext<CreateTradingAccountCampaignFormValues>()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const { campaignId, tradingAccount } = { ...values }
    setFieldValue('campaignId', campaignId)
    setFieldValue('tradingAccount', tradingAccount)

    if (account?.introducingBroker && value === account.introducingBroker.id) {
      setFieldValue('ibState', referral)
      setFieldValue('ibCode', account.introducingBroker.name)
      setFieldError('ibCode', '')
      setFieldValue('introducingBroker', account?.introducingBroker)
      setFieldValue('referralOptionId', referral.id)
    } else if (value === GenericReferralType.New) {
      setFieldValue('ibState', { id: '1', name: t('New Referral') })
      setAllFieldsDisabled(true)
      setFieldValue('ibCode', '')
      setFieldError('ibCode', '')
      setFieldValue('referralOptionId', GenericReferralType.New)
    } else {
      setFieldValue('ibState', referral)
      setAllFieldsDisabled(false)
      setFieldValue('ibCode', '')
      setFieldValue('introducingBroker', undefined)
      setFieldValue('referralOptionId', GenericReferralType.None)
    }

    if (isOnlyOnePlatformsAllowToOpenAccount && platformAllowToOpenAccount) {
      setFieldValue('platformType', platformAllowToOpenAccount.platform)
    }
  }

  return (
    <RadioButton
      name={referral.id}
      label={referral.name}
      value={referral.id}
      checked={values?.ibState?.id === referral.id}
      disabled={isValidatingIBCode}
      onChange={handleChange}
      key={referral.id}
    />
  )
}

function dataToSubmit(
  values: CreateTradingAccountCampaignFormValues
): FormSubmitValues<CreateTradingAccount | CreateCampaignTradingAccount> {
  const campaignId = values.campaignId && values.campaignId === '0' ? null : values.campaignId
  const data = {
    introducingBrokerId: values.introducingBroker?.id || null,
    receivesReports: true,
    leverageTypeId: values.leverageType?.id ?? null,
    platformTypeId: values.platformType?.id ?? null,
    tradingAccountTypeId: values.accountType?.id ?? null,
  }

  return campaignId
    ? { ...data, currencyId: values.currencyId!, campaignId }
    : { ...data, walletId: values.wallet?.id ?? null }
}

export const CreateTradingAccountCampaignForm = withFormik<
  OuterProps,
  CreateTradingAccountCampaignFormValues
>({
  mapPropsToValues: (props) => {
    return createInitialState(props)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    setSubmitting(true)
    try {
      if (values.referralOptionId === GenericReferralType.New && !values.ibCode) {
        return
      }
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const { hasCampaignQuery } = props
    const { campaignId } = values
    const validCampaignId = campaignId && campaignId !== '0'
    const errors: FormikErrors<CreateTradingAccountCampaignFormValues> = {}

    if (!validCampaignId && !values.wallet) {
      errors.wallet = t('Validation.Required')
    }

    if (validCampaignId && !values.currencyId) {
      errors.currencyId = t('Validation.Required')
    }

    if (!values.accountType) {
      errors.accountType = t('Validation.Required')
    }

    if (!values.platformType) {
      errors.platformType = t('Validation.Required')
    }

    if (!values.leverageType) {
      errors.leverageType = t('Validation.Required')
    }

    if (!values.terms && (hasCampaignQuery || validCampaignId)) {
      errors.terms = t('Validation.Required')
    }

    if (!values.markup) {
      errors.markup = t('Validation.Required')
    }

    if (values.referralOptionId === GenericReferralType.New && !values.ibCode) {
      errors.ibCode = t('Validation.Required')
    }

    if (values.ibCode && !IBPattern.test(values.ibCode)) {
      errors.ibCode = t('Validation.Referral code should begin with')
    }

    return errors
  },
  enableReinitialize: false,
  isInitialValid: false,
})(CreateTradingAccountCampaignFormUI)

const createInitialState = (props: OuterProps): CreateTradingAccountCampaignFormValues => {
  const { account, hasCampaignQuery, data, selectedPlatform, selectedCampaignId } = props

  const initialTradingAccount: CampaignExtendedNameDto = {
    id: '0',
    name: t('Trading Account.Regular Trading Account'),
    platformTypes: [],
  }

  const selectedCampaign = data.find((campaign) => campaign.id === selectedCampaignId)
  const defaultCampaign = data.find((campaign) => campaign.isCampaign)
  const campaignType = selectedCampaign || defaultCampaign || initialTradingAccount

  const shouldUseCampaign = hasCampaignQuery || selectedCampaignId
  const tradingAccount = shouldUseCampaign ? campaignType : initialTradingAccount

  const campaignId = tradingAccount.isCampaign ? tradingAccount.id : null
  const introducingBroker = account?.introducingBroker ? account.introducingBroker : undefined

  const values = {
    tradingAccount,
    campaignId,
    calculationType: campaignType?.calculationType?.id
      ? {
          id: campaignType.calculationType.id,
          name: campaignType.calculationType.name,
        }
      : undefined,
    introducingBroker,
    leverageType: undefined,
    leverageTypes: [],
    platformType: selectedPlatform,
    receivesReports: true,
    accountType: undefined,
    wallet: undefined,
    currencyId: undefined,
    ibCode: account?.introducingBroker ? account.introducingBroker.name : '',
    referralOptionId: account?.introducingBroker ? account.introducingBroker.id : '',
    terms: false,
    markup: true,
    ibState: createIbInitialState(),
  }

  return {
    ...values,
    ibState: createIbInitialState(values),
  }
}

const filterReferralOptions = (props: OuterProps) => (referral: NameDto<string>) => {
  // Means there is no Actual IB
  if (!props.account?.introducingBroker) {
    return referral.name !== t('Existing Referral')
  }

  return referral
}

enum GenericReferralType {
  Existing = '2',
  New = '1',
  None = '0',
}

const createIbInitialState = (values?: CreateTradingAccountCampaignFormValues) => {
  if (values?.introducingBroker) {
    return {
      id: values.introducingBroker.id,
      name: t('Existing Referral'),
    }
  }

  return {
    id: GenericReferralType.None,
    name: t('No Referral'),
  }
}

const createReferralOptions = (props: OuterProps) => {
  const options = [
    {
      id: GenericReferralType.New,
      name: t('New Referral'),
    },
    {
      id: GenericReferralType.None,
      name: props.account?.introducingBroker ? t('No Referral with message') : t('No Referral'),
    },
    {
      id: props?.account?.introducingBroker?.id || '',
      name: props.account?.introducingBroker
        ? t('Existing Referral with message')
        : t('Existing Referral'),
    },
  ]

  if (isTickmillUK(props.account)) {
    return options.filter((option) => option.id !== GenericReferralType.New)
  }

  return options
}
